import { AccessibilityWrapper } from 'legacy-packages/ft-cb';
import DeleteQuizModal from 'legacy/src/components/questionBank/QuizBuilder/components/QuizBuilderReview/DeleteQuizModal';
import SingleAction from 'chameleon/ui-stack/ui/assessments/actions/single-action';
import ConfirmDialog from 'chameleon/ui-stack/ui/modals/confirm-dialog';
import React, { useState, useRef, Fragment } from 'react';

const DeleteAction = ({
  onDelete,
  quizTitle = '',
  quizId,
  isUnassignedAssessment = false,
  onAssessmentDelete = () => {},
  isAllResourcesPage,
  sendTelemetryEvent = () => {},
  totalStudents = 0,
  displayNameOfClassOrStudent = '',
  isSharedAssessment,
  isVideo = false,
  client,
  disabled,
  displayQuizLabel,
}) => {
  const actionButton = useRef(null);

  const [showModal, setShowModal] = useState(false);
  const [showDeleteAssessmentModal, setShowDeleteAssessmentModal] =
    useState(false);
  const [showAlreadyAssignedModal, setShowAlreadyAssignedModal] =
    useState(false);

  const ariaLabelledBy = 'delete-assessment-warning delete-controls';

  const deleteQuestion = 'Are you sure you want to delete this assignment to';
  const deleteText =
    totalStudents > 1
      ? `${totalStudents} students in ${displayNameOfClassOrStudent}`
      : `${displayNameOfClassOrStudent}`;
  const deleteNote = isVideo
    ? 'It will not erase your students’ viewing activity, which will still appear if you assign this video again later.'
    : 'Note that you will also be deleting all student data associated with this assignment.';

  const onActionClick = (e) => {
    e.stopPropagation();
    sendTelemetryEvent();
    if (isUnassignedAssessment) {
      setShowDeleteAssessmentModal(!showDeleteAssessmentModal);
    } else if (!isUnassignedAssessment && isSharedAssessment) {
      setShowAlreadyAssignedModal(true);
    } else if (!isUnassignedAssessment && isAllResourcesPage) {
      /**
       * We have an assigned quiz on the unassigned page, this happens in the Calc
       * AB/ BC case where teacher authored assessments are shared between both. When
       * an assessment is asssigned in one subject and not in the other, and the teacher
       * tried to delete the assessment we end up inside there where we show a modal for
       * this unique case.
       */
      setShowModal(false);
      setShowAlreadyAssignedModal(true);
    } else {
      setShowModal(!showModal);
    }

    if (actionButton.current) {
      actionButton.current.focus();
    }
  };

  const onSubmit = () => {
    setShowModal(false);
    onDelete();
  };

  const onCloseAlreadyAssigned = () => {
    setShowAlreadyAssignedModal(false);
  };

  const deleteAssessment = () => {
    setShowDeleteAssessmentModal(false);
    onAssessmentDelete();
  };

  const deleteQuizOrAssignmentText =
    displayQuizLabel ||
    isUnassignedAssessment ||
    isSharedAssessment ||
    isAllResourcesPage
      ? 'Quiz'
      : 'Assignment';

  if (showAlreadyAssignedModal) {
    const modalTitle = isSharedAssessment ? 'Delete Quiz' : 'Already Assigned';
    const buttonText = isSharedAssessment ? 'OK' : 'Close';
    const deleteAssessmentText = isSharedAssessment
      ? 'You cannot delete a quiz that has been assigned to a class'
      : 'You cannot delete an assessment that you have already assigned in any course this academic year';

    return (
      <ConfirmDialog
        title={modalTitle}
        onClose={onCloseAlreadyAssigned}
        onSuccess={onCloseAlreadyAssigned}
        content={{
          btnPrimary: buttonText,
        }}
      >
        <p>{deleteAssessmentText}</p>
      </ConfirmDialog>
    );
  }

  return (
    <Fragment>
      {showModal && (
        <ConfirmDialog
          title={`Delete "${quizTitle}"`}
          contentWithSpacing={false}
          modalClassName="AssignModal ProgressControlModal"
          contentClass="AssignModal--content"
          onClose={onActionClick}
          onSuccess={onSubmit}
          aria={{
            labelledby: 'title',
            describedBy: 'delete-assignment',
          }}
          hasDangerPrimaryAction
          content={{
            btnPrimary: 'Yes',
            btnPrimaryType: 'danger',
            btnSecondary: 'No',
          }}
        >
          <div id="delete-assignment" aria-labelledby={ariaLabelledBy}>
            <p className="mx-6" id="delete-assessment-warning">
              {deleteQuestion} {deleteText}?
              <br />
              {deleteNote}
            </p>
          </div>
        </ConfirmDialog>
      )}
      {showDeleteAssessmentModal && (
        <DeleteQuizModal
          onCancel={() => setShowDeleteAssessmentModal(false)}
          onDeleteQuiz={deleteAssessment}
          quizTitle={quizTitle}
          quizId={quizId}
          client={client}
        />
      )}
      <AccessibilityWrapper>
        <SingleAction
          onClick={onActionClick}
          tabIndex={0}
          aria-label="Delete"
          ref={actionButton}
          iconName="delete"
          disabled={disabled}
        >
          <span className="item-text">Delete {deleteQuizOrAssignmentText}</span>
        </SingleAction>
      </AccessibilityWrapper>
    </Fragment>
  );
};

export default DeleteAction;
